import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  removeCard,
  markCardAsComplete,
  generateCardDetailsUrl,
  isValidCardId,
  updateCardWithForm,
  buildTagEditPatch,
  shouldShowLabelAsTag,
  buildDuplicateCardPrefill,
} from "@/lib/cards";
import { card as cardRepository } from "@/repositories/cards";
import { TagsRepository } from "@/repositories/tags";
import { firestoreToDateTime } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import {
  Card,
  CreateCardDto,
  PatchCardDto,
  Shop,
  TagsDocument,
} from "@/types/v2";

export type CardMenuDialog =
  | "form"
  | "remove"
  | "markComplete"
  | "metrics"
  | "tags"
  | "customerUpdate"
  | "customerUpdatesTimeline"
  | "flashColor"
  | "duplicate";

export const useCardMenuActions = (
  cardData: Card,
  shop: Shop,
  tags: TagsDocument | undefined,
  onMoveToClick?: (card: Card) => void
) => {
  const navigate = useNavigate();
  const { shopId } = useParams();
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialog, setDialog] = useState<CardMenuDialog>("form");

  const { toast } = useToast();

  const duplicatePrefill = useMemo(
    () => buildDuplicateCardPrefill(cardData),
    [cardData]
  );

  const handleDuplicateCard = async (values: CreateCardDto) => {
    // `duplicatedFrom`, `technicianIds`, and `subletSales` are stripped from
    // `values` by CardForm's zod resolver (its `formSchema` never declares
    // them, and Zod v3 plain-object schemas strip unknown keys) -- merge
    // them back in from the full `duplicatePrefill` computed above (C1).
    // `technicianIds`/`subletSales` are conditionally spread rather than
    // assigned outright, since `buildDuplicateCardPrefill` omits them
    // entirely (not `undefined`-valued) when the source card lacks them
    // (C2) -- assigning `duplicatePrefill.technicianIds` unconditionally
    // here would reintroduce an explicit `undefined` into the Firestore
    // write that C2 closed off in the prefill itself.
    if (values.tags?.length && values.tags?.length > 0) {
      await TagsRepository.addTag(shop.id, values.tags, tags);
    }

    const newCard = await cardRepository.create(shop.id, {
      ...values,
      duplicatedFrom: duplicatePrefill.duplicatedFrom,
      // Land the duplicate in the source card's own container instead of
      // `card.create`'s intake ("dispatch") default -- conditionally
      // spread, same as technicianIds/subletSales below, so a source card
      // with no container doesn't write an explicit `undefined`.
      ...(cardData.currentContainer !== undefined && {
        currentContainer: cardData.currentContainer,
      }),
      ...(duplicatePrefill.technicianIds !== undefined && {
        technicianIds: duplicatePrefill.technicianIds,
      }),
      ...(duplicatePrefill.subletSales !== undefined && {
        subletSales: duplicatePrefill.subletSales,
      }),
    });

    if (values.completedDate && newCard?.id) {
      const completedDate = firestoreToDateTime(values.completedDate);
      await markCardAsComplete(shop.id, newCard.id, completedDate);
    }

    toast({
      title: newCard?.cardNumber
        ? `Card duplicated to ${newCard.cardNumber}`
        : "Card duplicated",
    });
    setOpen(false);
  };

  const handleRemoveCard = async () => {
    await removeCard(shop.id, cardData.id);
    setOpen(false);
  };

  const handleMarkComplete = async () => {
    const completedDate = firestoreToDateTime(cardData.completedDate);
    await markCardAsComplete(shop.id, cardData.id, completedDate);
    setOpen(false);
  };

  const handleEditCard = async (values: PatchCardDto) => {
    await updateCardWithForm(shop.id, cardData.id, values, cardData, tags);
    setOpen(false);
  };

  const handleEditTag = async (values: { tags: string[] }) => {
    await TagsRepository.addTag(shop.id, values.tags, tags);

    await cardRepository.update(
      shop.id,
      cardData.id,
      buildTagEditPatch(cardData, values)
    );
    setOpen(false);
    return;
  };

  const handleToggleLabelAsTag = async () => {
    await cardRepository.update(shop.id, cardData.id, {
      _isSoftMigrated: cardData._isSoftMigrated,
      labelAsTagEnabled: !shouldShowLabelAsTag(cardData, shop),
    });
    setOpen(false);
    return;
  };

  const handleResetCard = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    await cardRepository.update(shop.id, cardData.id, {
      _isSoftMigrated: cardData._isSoftMigrated,
      automationAction: null,
      automationActionData: null,
      automationActionStartAt: null,
      automationActionExpireAt: null,
      manualAction: null,
      manualActionData: null,
      manualActionStartAt: null,
    });
    setOpen(false);
    return;
  };

  const handleForceEffect = async (
    action: "flash" | "wiggle",
    color: string
  ) => {
    // Close synchronously, before the write resolves -- a caller with no
    // dialog step (e.g. Wiggle's direct menu item, WEBV3-311) needs the menu
    // to close immediately on click rather than lingering open until the
    // Firestore round-trip completes.
    setDialogOpen(false);
    setOpen(false);
    await cardRepository.update(shop.id, cardData.id, {
      _isSoftMigrated: cardData._isSoftMigrated,
      manualAction: action,
      manualActionData: color,
      manualActionStartAt: new Date(),
    });
    return;
  };

  const handleOpenDialog = (nextDialog: CardMenuDialog) => {
    setDialog(nextDialog);
    setDialogOpen(true);
    setOpen(false); // Close the dropdown menu
  };

  const handleViewDetails = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!shopId || !isValidCardId(cardData.id)) {
      console.error("Invalid shop ID or card ID for navigation", {
        shopId,
        cardId: cardData.id,
      });
      return;
    }

    const cardUrl = generateCardDetailsUrl(shopId, cardData.id);

    // Handle Ctrl/Cmd+click for opening in new tab to preserve dashboard state
    if (event.ctrlKey || event.metaKey) {
      window.open(cardUrl, "_blank");
    } else {
      navigate(cardUrl);
    }

    setOpen(false);
  };

  const handleMoveToClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (onMoveToClick) {
      onMoveToClick(cardData);
    }

    setOpen(false);
  };

  return {
    open,
    setOpen,
    dialogOpen,
    setDialogOpen,
    dialog,
    isLabelShownAsTag: shouldShowLabelAsTag(cardData, shop),
    duplicatePrefill,
    handleRemoveCard,
    handleMarkComplete,
    handleEditCard,
    handleEditTag,
    handleToggleLabelAsTag,
    handleResetCard,
    handleForceEffect,
    handleDuplicateCard,
    handleOpenDialog,
    handleViewDetails,
    handleMoveToClick,
  };
};
